import React, { useState, useEffect } from "react";
import {
  Calendar,
  Phone,
  HeartPulse,
  Brain,
  Bone,
  Baby,
  User,          // <-- Correct replacement
  Stethoscope,
  Pill,
  Ear,
  ChevronRight,
  MessageSquare,
  Scissors,
  Activity,
  Droplet,
  Wind,
  Scan,
  Dumbbell,
  Layers,
  Syringe,
  Beaker,
  Apple,
  Heart,
  Bike,
  Smile,
  Radio
} from "lucide-react";
 
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { HeaderNav } from "../components/header-nav";
import { Badge } from "../components/ui/badge";
import SEO from "./SEO";
 
const WP_API_BASE = "/wp-json/wp/v2";
 
const getIcon = (name) => {
  const cleanName = name.trim();
  const iconMap = {
    "Cardiology": HeartPulse,
    "Neurology": Brain,
    "Orthopedics": Bone,
    "Paediatric": Baby,
    "Paediatric Surgery": Scissors,
    "Gynaecology": User,
    "Dentistry": Smile,
    "Gastroenterology": Droplet,
    "General Surgery": Scissors,
    "General Medicine": Pill,
    "Neonatologist": Baby,
    "Neonatology": Baby,
    "Prenatal Yoga": Bike,
    "Critical Care": Activity,
    "Pathology": Beaker,
    "Neurosurgery": Brain,
    "Oncology": Heart,
    "Urology & Nephrology": Droplet,
    "Laparoscopic Surgery": Layers,
    "Physiotherapy": Dumbbell,
    "Dietetics": Apple,
    "Dermatology": Scan,
    "ENT": Ear,
    "Endocrinology": Syringe,
    "Pulmonology": Wind,
    "Plastic & Cosmetic Surgery": Scissors,
    "Psychology": Brain,
    "Radiology": Radio
  };
  return iconMap[cleanName] || Stethoscope;
};
 
export default function ServicesPage() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seo, setSeo] = useState({});
  const [pageKeywords, setPageKeywords] = useState("");
 
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const catRes = await fetch(`${WP_API_BASE}/categories?slug=services`);
        const categories = await catRes.json();
       
        if (categories.length > 0) {
          const categoryId = categories[0].id;
          const postsRes = await fetch(`${WP_API_BASE}/posts?categories=${categoryId}&_embed&per_page=100&acf_format=standard`);
          const posts = await postsRes.json();
         
          const mapped = posts.map(post => ({
            name: post.title.rendered,
            icon: getIcon(post.title.rendered),
            description: post.acf?.hero_description || "",
            services: post.acf?.why_choose_card_repeater?.map(item => item.title) || [],
            image: post.acf?.hero_image || "/placeholder.svg",
            href: `/services/${post.slug}`
          }));
 
          mapped.sort((a, b) => a.name.localeCompare(b.name));
 
          setDepartments(mapped);
        }
      } catch (err) {
        console.error("Error fetching services:", err);
      } finally {
        setLoading(false);
      }
    };
 
    fetchServices();

    fetch("/wp-json/custom/v1/page-seo?slug=services")
      .then(res => res.json())
      .then(data => { if (data?.seo) setSeo(data.seo); setPageKeywords(data?.keywords || ""); })
      .catch(() => {});
  }, []);
 
  const servicesPageSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/",
    "logo": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad offering a comprehensive range of healthcare services.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "appointments",
        "telephone": "+91-99660-60000",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      },
      {
        "@type": "ContactPoint",
        "contactType": "emergency",
        "telephone": "+91-98485-98285",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Pranaam Hospitals Healthcare Services",
    "url": "https://pranaamhospitals.com/services",
    "description": "Browse the wide range of healthcare services offered by Pranaam Hospitals in Hyderabad including specialties, diagnostics, emergency care, and more.",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Cardiology",
          "url": "https://pranaamhospitals.com/services/cardiology"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Neurology",
          "url": "https://pranaamhospitals.com/services/neurology"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Orthopedics",
          "url": "https://pranaamhospitals.com/services/orthopedics"
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": "Pediatrics",
          "url": "https://pranaamhospitals.com/services/paediatric"
        },
        {
          "@type": "ListItem",
          "position": 5,
          "name": "General Medicine",
          "url": "https://pranaamhospitals.com/services/general-medicine"
        },
        {
          "@type": "ListItem",
          "position": 6,
          "name": "Critical Care",
          "url": "https://pranaamhospitals.com/services/critical-care"
        },
        {
          "@type": "ListItem",
          "position": 7,
          "name": "Diagnostics & Pathology",
          "url": "https://pranaamhospitals.com/services/pathology"
        },
        {
          "@type": "ListItem",
          "position": 8,
          "name": "Physiotherapy",
          "url": "https://pranaamhospitals.com/services/physiotherapy"
        }
        // Add remaining services if needed
      ]
    }
  }
];
 
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
 
  return (
    <>
    <SEO
      title={seo.title || "Our Healthcare Services in Hyderabad | Pranaam Hospitals"}
      description={seo.description || "Explore the wide range of healthcare services offered by Pranaam Hospitals in Hyderabad, including cardiology, neurology, orthopedics, pediatrics, surgery, diagnostics, and more."}
      canonical={seo.canonical || "https://pranaamhospitals.com/services"}
      robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
      keywords={pageKeywords}
      ogTitle={seo.og_title}
      ogDescription={seo.og_description}
      ogImage={seo.og_image || seo.featured_image}
      twitterTitle={seo.twitter_title}
      twitterDescription={seo.twitter_description}
      author="Pranaam Hospitals"
      publisher="Pranaam Hospitals"
      schema={servicesPageSchema}
    />
 
    <div className="min-h-screen bg-white">
     <main>
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 bg-gradient-to-r from-primary/10 to-primary/5">
          <div className="container px-4 mx-auto text-center">
            <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
              <Stethoscope className="w-4 h-4 mr-1" />
              Our Specialties
            </Badge>
 
            <h1 className="mb-4 text-4xl md:text-5xl lg:text-6xl font-bold font-heading">
              Comprehensive <span className="text-primary">Healthcare Services</span>
            </h1>
 
            <p className="mb-8 text-lg text-gray-700">
              At Pranaam Hospitals, we offer a wide range of specialized medical services across{" "}
              {departments.length}+ departments.
            </p>
 
             <Button size="lg" asChild>
                <a
                href="/book-now">
                <Calendar className="w-5 h-5" />
                Book an Appointment
              </a>
            </Button>
          </div>
        </section>
 
        {/* SERVICES GRID */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {departments.map((dept, index) => {
                const IconComponent = dept.icon;
 
                return (
                  <Card key={index} className="overflow-hidden hover:shadow-lg transition-all group">
                    <div className="relative h-72 bg-gray-100">
                      <img
                        src={dept.image}
                        alt={`${dept.name} department at Pranaam Hospitals`}
                        width="600"
                        height="400"
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
 
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                        <div className="flex items-center gap-2 w-full">
                          <IconComponent className="w-5 h-5 text-white" />
                          <h2
                            className="text-xl font-bold text-white leading-snug line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: dept.name }}
                          />
                        </div>
                      </div>
                    </div>
 
                    <CardContent className="p-6">
                      <p className="mb-4 text-gray-700 line-clamp-3">{dept.description}</p>
 
                      <ul className="mb-6 space-y-2 min-h-[100px]">
                        {dept.services.map((service, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                            <span className="text-sm">{service}</span>
                          </li>
                        ))}
                      </ul>
 
                     <Button
                        asChild
                        className="w-full min-h-[52px] text-base"
                      >
                        <a
                          href={dept.href}
                          className="flex items-center justify-center gap-2 px-4 py-3 text-center whitespace-normal break-words"
                          aria-label={`Learn more about ${dept.name} services`}
                        >
                          Learn More
                          <ChevronRight className="w-4 h-4 shrink-0" />
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
 
        {/* WHY CHOOSE US */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <Badge className="mb-4">Why Choose Us</Badge>
 
              <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4">
                Excellence in <span className="text-primary">Every Department</span>
              </h2>
 
              <p className="text-gray-700">
                Our commitment to providing world-class healthcare across all specialties sets us apart.
              </p>
            </div>
 
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <Stethoscope className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Expert Specialists</h3>
                  <p className="text-sm text-gray-700">Highly qualified doctors with years of experience.</p>
                </CardContent>
              </Card>
 
              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <Activity className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Advanced Technology</h3>
                  <p className="text-sm text-gray-700">Cutting-edge diagnostic and treatment systems.</p>
                </CardContent>
              </Card>
 
              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <HeartPulse className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Patient-Centric Care</h3>
                  <p className="text-sm text-gray-700">Compassionate care focused on comfort and recovery.</p>
                </CardContent>
              </Card>
 
              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <Calendar className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">24/7 Availability</h3>
                  <p className="text-sm text-gray-700">Emergency and critical care round the clock.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
 
        {/* CTA SECTION */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <Card className="shadow-xl">
              <div className="p-12 bg-gradient-to-r from-primary to-black text-center text-white">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 font-heading">Ready to Get Started?</h2>
 
                <p className="text-lg mb-8 opacity-90 max-w-xl mx-auto">
                  Book an appointment with our specialists and experience world-class healthcare.
                </p>
 
                <div className="flex flex-wrap justify-center gap-4">
                  <Button size="lg" variant="secondary" asChild>
                    <a
                      href="/book-now"
                      className="flex items-center gap-2 min-h-[48px] px-6"
                      aria-label="Book appointment at Pranaam Hospitals"
                    >
                      <Calendar className="w-5 h-5" />
                      Book Appointment
                    </a>
                  </Button>

                  <Button
                    size="lg"
                    variant="outline"
                    asChild
                    className="bg-white/10 border-white text-white hover:bg-white hover:text-primary"
                  >
                    <a
                      href="/contact"
                      className="flex items-center justify-center min-h-[48px] px-6"
                      aria-label="Contact Pranaam Hospitals"
                    >
                      Contact Us
                    </a>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </main>
 
 
      {/* WHATSAPP BUTTON */}
      {/* <div className="fixed bottom-6 right-6">
        <a
          href="https://wa.me/919848598285"
          className="w-14 h-14 bg-green-500 flex items-center justify-center rounded-full shadow-lg hover:bg-green-600"
        >
          <MessageSquare className="w-7 h-7 text-white" />
        </a>
      </div> */}
    </div>
    </>
  );
}